/**
 * Centralized composable for allocation calculations
 * Handles percentage, amount, and decimal precision consistently across the product
 */
export function useAllocationCalculations() {
  // Product-wide decimal precision settings
  const PERCENTAGE_SCALE = 100;
  const PERCENTAGE_PRECISION = 8; // Store percentages with 8 decimal places for accuracy
  const DISPLAY_PRECISION = 2; // Display percentages rounded to 2 decimals

  /**
   * Rounds a number to specified decimal places using consistent rounding
   * @param {number} value - Value to round
   * @param {number} decimals - Number of decimal places (default: 2)
   * @returns {number} Rounded value
   */
  const roundToDecimals = (value, decimals = 2) => {
    const factor = Math.pow(10, decimals);
    return Math.round((Number(value) || 0) * factor) / factor;
  };

  /**
   * Rounds a percentage to 8 decimal places for internal storage
   * This ensures 3 thirds (33.33333333%) total exactly 100.00%
   * @param {number} value - Percentage value to round
   * @returns {number} Percentage rounded to 8 decimals
   */
  const roundPercentageForStorage = value => {
    return roundToDecimals(value, PERCENTAGE_PRECISION);
  };

  /**
   * Distributes total amount evenly across allocations
   * @param {number} totalAmount - Total amount to distribute
   * @param {number} count - Number of allocations
   * @returns {Object} Object with baseAmount, basePercentage, and remainders
   */
  const distributeEvenly = (totalAmount, count) => {
    if (count <= 0) return {
      baseAmount: 0,
      basePercentage: 0,
      amountRemainder: 0,
      percentageRemainder: 0
    };
    const baseAmount = roundToDecimals(totalAmount / count);
    // Store percentages with 8 decimal precision for accuracy
    const basePercentage = roundPercentageForStorage(PERCENTAGE_SCALE / count);
    const amountRemainder = roundToDecimals(totalAmount - baseAmount * count);
    // Calculate remainder using full precision percentages, then round to 2 decimals for display
    const percentageRemainder = roundToDecimals(PERCENTAGE_SCALE - basePercentage * count, DISPLAY_PRECISION);
    return {
      baseAmount,
      basePercentage,
      amountRemainder,
      percentageRemainder
    };
  };

  /**
   * Calculates totals for allocations
   * Sums full-precision percentages internally, then rounds total to 2 decimals for display
   * @param {Array} allocations - Array of allocation objects
   * @returns {Object} Totals object with percentage, amount, workedHours, workedAmount
   */
  const calculateTotals = allocations => {
    if (!Array.isArray(allocations)) {
      return {
        percentage: 0,
        amount: 0,
        workedHours: 0,
        workedAmount: 0
      };
    }
    const totals = allocations.reduce((sums, alloc) => ({
      percentageRaw: sums.percentageRaw + (Number(alloc.percentage) || 0),
      amount: sums.amount + (Number(alloc.amount) || 0),
      workedHours: sums.workedHours + (Number(alloc.workedHours) || 0),
      workedAmount: sums.workedAmount + (Number(alloc.workedAmount) || 0)
    }), {
      percentageRaw: 0,
      amount: 0,
      workedHours: 0,
      workedAmount: 0
    });

    // Round the summed percentages to 2 decimals for validation/display
    // This ensures 3 thirds sum to exactly 100.00
    return {
      percentage: roundToDecimals(totals.percentageRaw, DISPLAY_PRECISION),
      amount: roundToDecimals(totals.amount),
      workedHours: roundToDecimals(totals.workedHours),
      workedAmount: roundToDecimals(totals.workedAmount)
    };
  };
  return {
    // Constants
    PERCENTAGE_SCALE,
    PERCENTAGE_PRECISION,
    DISPLAY_PRECISION,
    // Utility functions
    roundToDecimals,
    roundPercentageForStorage,
    // Calculation functions
    distributeEvenly,
    calculateTotals
  };
}